import React, { useContext, useState, useEffect, useMemo } from "react";
import { Row, Col } from "react-bootstrap";
import { GridColumn } from "@progress/kendo-react-grid";
import { UserContext } from "shared/context/UserContext";
import { useReactQueryHelpers } from "shared/hooks/useReactQueryHelpers";

import { Alert } from "react-bootstrap";

import {
  Base64ToArrayBuffer,
  SaveByteArray,
} from "shared/helpers/Base64ArrayTranslator";
import { StringToDate, DateWithoutTime } from "shared/helpers/DateHelpers";
import { IsNullOrWhitespace } from "shared/helpers/StringHelpers";
import { IsNullOrEmpty } from "shared/helpers/NullEmptyHelpers";

import SharedMainSelectableClientGrid from "shared/components/grids/clientGrid/SharedMainSelectableClientGrid";

import { useFetchExportData } from "./hooks/useFetchExportData";

import {
  ApplicationInsights,
  LogError,
} from "../../../components/AppInsights/TelemetryService";

import {
  DefaultColumnWidth,
  DefaultDateColumnWidth,
  DefaultNoFilterColumnWidth,
} from "../../../components/grid/Kendo/KendoGridAndColumnConstants";

export default function ExcelExportDownloads() {
  // const [rowKey, setRowKey] = useState("");
  const userContext = useContext(UserContext);

  const { invalidateReactQueryKey } = useReactQueryHelpers();

  const [downloadingRowKey, setDownloadingRowKey] = useState(null);

  function onDownloadError(error) {
    LogError(error);
  }

  const clientGridApiParams = useMemo(() => {
    if (IsNullOrEmpty(userContext.email)) {
      return {};
    }

    return {
      email: userContext.email,
    };
  }, [downloadingRowKey]);

  const clientGridExportApiParams = useMemo(() => {
    if (IsNullOrEmpty(userContext.email)) {
      return {};
    }

    if (IsNullOrEmpty(downloadingRowKey)) {
      return {};
    }

    return {
      email: userContext.email,
      rowKey: downloadingRowKey,
    };
  }, [downloadingRowKey]);

  const { data: exportData, isError: isExportError } = useFetchExportData(
    clientGridExportApiParams,
    onDownloadError
  );

  useEffect(() => {
    if (IsNullOrEmpty(downloadingRowKey)) {
      return;
    }

    if (IsNullOrEmpty(exportData)) {
      return;
    }

    //if we get here we have the data and we can download the file
    let fileStream = exportData.fileStream;
    let fileName = exportData.fileName;

    let dataAsArray = Base64ToArrayBuffer(fileStream);
    SaveByteArray(fileName, dataAsArray);

    //reset the downloading row key after data is downloaded
    setDownloadingRowKey(null);

    //forced a requery of the data
    invalidateReactQueryKey("api/File/GetExcelExportAsString");
  }, [downloadingRowKey, exportData]);

  function convertReportParametersToFriendlyString(parametersObject) {
    var finalStringOutput = "";

    for (const [key, value] of Object.entries(parametersObject)) {
      if (key !== "User") {
        finalStringOutput = finalStringOutput + `${key}: ${value}` + ", ";
      }
    }

    finalStringOutput = finalStringOutput.slice(0, -2);

    return finalStringOutput;
  }

  function RequestsDataProcessor(responseData) {
    let dataWithCleanedDates = responseData.map((item) => {
      var reportParameters = JSON.parse(item.reportParameters);

      return {
        ...item,
        CleanedCompletedDate: DateWithoutTime(
          StringToDate(item.creationTimeUtc)
        ),
        CleanedDownloadedDate: IsNullOrWhitespace(item.downloadTimeUtc)
          ? ""
          : DateWithoutTime(StringToDate(item.downloadTimeUtc)),
        UserFriendlyParameters:
          convertReportParametersToFriendlyString(reportParameters),
      };
    });

    return dataWithCleanedDates;
  }

  let DownloadDataCell = (props) => {
    let dataRow = props.dataItem;

    let downloadableStatuses = ["Completed", "Downloaded"];

    if (downloadableStatuses.indexOf(dataRow.requestStatus) > -1) {
      return (
        <td
          {...props.tdProps}
          colSpan={1}
          style={{
            color: "#015CAB",
          }}
        >
          <a
            style={{
              cursor: "pointer",
            }}
            onClick={() => setDownloadingRowKey(dataRow.rowKey)}
          >
            Download
          </a>
        </td>
      );
    } else if (dataRow.requestStatus === "Failure") {
      // The generator gave up on this request after exhausting its retries, so there is
      // nothing to download. Show it as failed rather than the misleading "Pending".
      return (
        <td {...props.tdProps} colSpan={1}>
          <span className="text-danger">Failed</span>
        </td>
      );
    } else {
      return (
        <td {...props.tdProps} colSpan={1}>
          <a>Pending</a>
        </td>
      );
    }
  };

  let StatusDataCell = (props) => {
    let dataRow = props.dataItem;

    if (dataRow.requestStatus === "Failure") {
      return (
        <td {...props.tdProps} colSpan={1}>
          <span className="text-danger">{dataRow.requestStatus}</span>
        </td>
      );
    }

    return (
      <td {...props.tdProps} colSpan={1}>
        {dataRow.requestStatus}
      </td>
    );
  };

  useEffect(() => {
    ApplicationInsights.trackPageView({ name: "ExcelExportDownloads" });
  }, []);

  return (
    <div className="feature">
      {!IsNullOrEmpty(isExportError) && isExportError && (
        <Row>
          <Col>
            <Alert variant={"danger"} dismissible>
              Download did not succeed.
            </Alert>
          </Col>
        </Row>
      )}

      <Row>
        <Col md={10} className="tight-grid multi-line-filter">
          <SharedMainSelectableClientGrid
            apiGetEndpoint={"api/File/GetAllRequestIds"}
            apiParameters={clientGridApiParams}
            aggregateColumnSettings={null}
            onDataRetrievedProcessor={RequestsDataProcessor}
            sendAggregatesParentCallback={() => {
              return null;
            }}
            styleOverride={{ minHeight: "19em", maxHeight: "50em" }}
            exportFileNamePrefix={"ExcelExportDownloadsClientGrid"}
          >
            <GridColumn
              key={""}
              field={""}
              title={"Download"}
              filterable={false}
              cells={{
                data: DownloadDataCell,
              }}
              width={DefaultNoFilterColumnWidth()}
            ></GridColumn>
            <GridColumn
              key={"reportName"}
              field={"reportName"}
              title={"Report Name"}
              width={DefaultColumnWidth()}
            ></GridColumn>
            <GridColumn
              key={"UserFriendlyParameters"}
              field={"UserFriendlyParameters"}
              title={"Report Parameters"}
              width={DefaultColumnWidth()}
            ></GridColumn>
            <GridColumn
              key={"requestStatus"}
              field={"requestStatus"}
              title={"Status"}
              cells={{
                data: StatusDataCell,
              }}
              width={DefaultColumnWidth()}
            ></GridColumn>
            <GridColumn
              key={"CleanedCompletedDate"}
              field={"CleanedCompletedDate"}
              filter="date"
              format="{0:d}"
              title={"Completed Date"}
              width={DefaultDateColumnWidth()}
            ></GridColumn>
            <GridColumn
              key={"CleanedDownloadedDate"}
              field={"CleanedDownloadedDate"}
              filter="date"
              format="{0:d}"
              title={"Download Date"}
              width={DefaultDateColumnWidth()}
            ></GridColumn>
          </SharedMainSelectableClientGrid>
        </Col>
      </Row>
    </div>
  );
}
